import { useContext, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { FINANCIALS_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { EXTERNAL_VALUATION_REFERENCE_TITLE, EXTERNAL_VALUATION_REFERENCE_TYPE } from 'common/constants/documents';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import { CustomContainer, CustomScalarSpreadsheet } from 'components';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { EXTERNAL_VALUATION_SPREADSHEET_SLUG } from 'pages/ValuationsAllocation/common/constants/externalValuation';
import { useValuationNotesCache } from 'pages/ValuationsAllocation/hooks';
import { getApproachTableName } from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import { getArrayValue, getObjectValue, getStringValue } from 'utilities';
import { ExternalValuationProps } from './types';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const ExternalValuation = (props: ExternalValuationProps) => {
  const { approach, isLoading, isSaving, onChange, spreadsheets, workbook } = props;
  const { panelId: approachPanelId, isDisabled = false } = getObjectValue(approach);
  const { externalValuation: externalValuationSpreadsheet } = getObjectValue(spreadsheets);

  const {
    measurementDate,
    filesInApproach,
    notesInApproach,
    setFilesInApproach,
    setNotesInApproach,
    registerApproachFormat,
  } = useContext(ValuationContext);
  const [format, formatDispatch] = useFormat({ page: FINANCIALS_CURRENCY_PAGE });
  const { isExportMode } = useLayoutContext();

  const { filesToSave, setFilesToSave } = useDocuments();
  const { notes, setNotes, notesHasChanged, setNotesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { notes: cachedNotes } = useValuationNotesCache({
    cachedNotes: notesInApproach,
    notes,
    notesHasChanged,
    panelId: approachPanelId,
    setCachedNotes: setNotesInApproach,
    setNotes,
    setNotesHasChanged,
  });

  const externalValuationKey = useMemo(
    () => getStringValue(externalValuationSpreadsheet?.name),
    [externalValuationSpreadsheet]
  );

  const externalValuationSheetProps = useMemo(() => {
    if (externalValuationSpreadsheet) {
      return {
        ...externalValuationSpreadsheet,
        format,
        formatDispatch,
        onChange,
        sheet: externalValuationSpreadsheet,
        workbook,
      } as unknown as typeof ScalarSpreadsheet;
    }

    return null;
  }, [externalValuationSpreadsheet, format, formatDispatch, onChange, workbook]);

  // Set Approach Document Files
  useEffect(() => {
    if (!isEmpty(filesToSave)) {
      setFilesInApproach?.(prevState => [{ ...prevState, filesToSave, panelId: approachPanelId }]);
    }
  }, [filesToSave, approachPanelId, setFilesInApproach]);

  // Set Document Files to Save
  useEffect(() => {
    if (!isEmpty(filesInApproach)) {
      const approachFiles = filesInApproach?.filter(file => file.panelId === approachPanelId);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: setFilesToSave is not typed
      setFilesToSave?.(getArrayValue(approachFiles?.[0]?.filesToSave));
    } else {
      setFilesToSave?.([]);
    }
  }, [filesInApproach, approachPanelId, setFilesToSave]);

  useEffect(() => {
    const tabId = getApproachTableName({ approach });
    registerApproachFormat?.(tabId, format, formatDispatch);
  }, [approach, format, formatDispatch, registerApproachFormat]);

  useExportSignal({
    target: PdfExportTargets.EXTERNAL_VALUATION.id,
    status: isLoading || !externalValuationSpreadsheet?.data ? 'loading' : 'ready',
  });

  return (
    <CustomContainer
      className={`${VALUATIONS_SLUG}-${EXTERNAL_VALUATION_SPREADSHEET_SLUG}-container`}
      data-ref={PdfExportTargets.EXTERNAL_VALUATION.id}
      style={isExportMode ? EXPORT_MODE_STYLES : {}}>
      <CustomScalarSpreadsheet
        key={externalValuationKey}
        {...externalValuationSheetProps}
        isLoading={isLoading}
        isSaving={isSaving}
      />
      <WidgetDrawer
        applicationContext={WidgetDrawerApplicationContext.EXTERNAL_VALUATION}
        notesProps={{
          isApproach: true,
          isDisabled,
          notes: cachedNotes,
          onAddNote,
          onDeleteNote,
          onUpdateNotes,
          pageType: VALUATIONS_PAGE_VALUE,
          pageTypeId: approach?.id,
          pageTypeKey: VALUATIONS_PAGE_KEY,
          setNotes,
        }}
        documentReferencesProps={{
          currentPage: EXTERNAL_VALUATION_REFERENCE_TITLE,
          filesToSave,
          isDisabled,
          referencedFeatureId: approach?.id,
          referenceType: EXTERNAL_VALUATION_REFERENCE_TYPE,
          selectedMeasurementDate: measurementDate,
          setFilesToSave,
        }}
      />
    </CustomContainer>
  );
};

export default ExternalValuation;
